import { cn } from '@/lib/utils';
import { Check, ChevronDown } from 'lucide-react';
import { forwardRef, useId, type ReactNode } from 'react';

const controlClass =
  'h-12 w-full min-w-0 rounded-[12px] border border-line-2 bg-surface px-3.5 text-sm text-ink placeholder:text-muted outline-none transition-[border-color,box-shadow] duration-200 focus:border-brand focus:ring-[3px] focus:ring-brand/20 disabled:opacity-45 aria-invalid:border-destructive';

interface FieldShellProps {
  id?: string;
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function FieldShell({
  id,
  label,
  error,
  hint,
  className,
  children,
}: FieldShellProps) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      {label && (
        <label htmlFor={id} className="text-sm font-semibold text-body">
          {label}
        </label>
      )}
      {children}
      {(error || hint) && (
        <span className={cn('text-xs', error ? 'text-destructive' : 'text-muted')}>
          {error || hint}
        </span>
      )}
    </div>
  );
}

type TextFieldProps = React.ComponentProps<'input'> & {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  wrapperClassName?: string;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, hint, wrapperClassName, className, id, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;

    return (
      <FieldShell
        id={inputId}
        label={label}
        error={error}
        hint={hint}
        className={wrapperClassName}
      >
        <input
          ref={ref}
          id={inputId}
          aria-invalid={!!error || undefined}
          className={cn(controlClass, className)}
          {...props}
        />
      </FieldShell>
    );
  },
);
TextField.displayName = 'TextField';

type SelectFieldProps = React.ComponentProps<'select'> & {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  wrapperClassName?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
};

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    {
      label,
      error,
      hint,
      wrapperClassName,
      className,
      id,
      options,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const selectId = id ?? autoId;

    return (
      <FieldShell
        id={selectId}
        label={label}
        error={error}
        hint={hint}
        className={wrapperClassName}
      >
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            aria-invalid={!!error || undefined}
            className={cn(
              controlClass,
              'cursor-pointer appearance-none pr-10',
              className,
            )}
            {...props}
          >
            {placeholder !== undefined && <option value="">{placeholder}</option>}
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted" />
        </div>
      </FieldShell>
    );
  },
);
SelectField.displayName = 'SelectField';

export interface ChoiceOption<T> {
  value: T;
  label: string;
}

interface ChoiceGroupProps<T> {
  label?: ReactNode;
  options: ChoiceOption<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  error?: string;
  /** Duas opções ficam limitadas a 420px, como no design. */
  compact?: boolean;
  className?: string;
}

/** Escolha única em cards selecionáveis (radio group estilizado). */
export function ChoiceGroup<T extends string | boolean>({
  label,
  options,
  value,
  onChange,
  error,
  compact = options.length === 2,
  className,
}: ChoiceGroupProps<T>) {
  const name = useId();

  return (
    <div className={cn('flex min-w-0 flex-col gap-2.5', className)}>
      {label && <span className="text-sm font-semibold text-body">{label}</span>}
      <div
        role="radiogroup"
        className={cn(
          'grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]',
          compact && 'max-w-[420px]',
        )}
      >
        {options.map((o) => {
          const selected = value === o.value;
          return (
            <button
              key={`${name}-${String(o.value)}`}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(o.value)}
              className={cn(
                'flex h-[52px] w-full items-center justify-between gap-4 rounded-[12px] border px-3.5 text-left transition-all duration-200',
                selected
                  ? 'border-brand/55 bg-brand-tint'
                  : 'border-line-2 bg-transparent hover:border-brand/30',
              )}
            >
              <span className="text-sm font-semibold text-ink">{o.label}</span>
              <span
                className={cn(
                  'flex size-6 flex-none items-center justify-center rounded-full border',
                  selected ? 'border-brand-fill text-brand-fill' : 'border-line-2',
                )}
              >
                {selected && <Check className="size-3.5" strokeWidth={2.4} />}
              </span>
            </button>
          );
        })}
      </div>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

interface FormSectionProps {
  eyebrow: string;
  title: string;
  children: ReactNode;
  className?: string;
}

/** Fieldset com eyebrow numerado ("01 • O CASAL") e título. */
export function FormSection({ eyebrow, title, children, className }: FormSectionProps) {
  return (
    <fieldset
      className={cn(
        'm-0 flex min-w-0 flex-col gap-5 rounded-[14px] border border-line bg-surface p-7',
        className,
      )}
    >
      <legend className="float-left flex w-full flex-col gap-1 p-0">
        <span className="text-[11px] font-bold tracking-[.09em] text-brand">
          {eyebrow}
        </span>
        <span className="font-display text-xl font-bold text-ink">{title}</span>
      </legend>
      {children}
    </fieldset>
  );
}
